import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import { PlateComponentModule } from '../plate/plate.module';

import { SearchResultTableComponent } from './search-result-table.component';
import { SearchResultRowComponent } from '../search-result-row/search-result-row.component';
import { TermListResultRowComponent } from '../term-list-result-row/term-list-result-row.component';
import { InlineFavouriteComponent } from '../inline-favourite/inline-favourite.component';
import { InlinePlateRankComponent } from '../inline-plate-rank/inline-plate-rank.component';
import { InlinePlateViewerLinkComponent } from '../inline-plate-viewer-link/inline-plate-viewer-link.component';
import { SeeMoreButtonComponent } from '../see-more-button/see-more-button.component';
import { SignedUpContentComponent } from '../signed-up-content/signed-up-content.component';

/**
 * Shared module owning the canonical search-results table — the row
 * layout (plate / favourite / plate-viewer / rank / price / View)
 * used on /build-your-own, /search-results/:term, and now the inline
 * BYO results on /new-release/:year.
 *
 * Extracted from app.module so feature modules (e.g. new-release-page-v2)
 * can import `<pla-search-result-table>` without re-declaring its
 * eight transitive components.
 *
 * Declares (every component in the table's render tree):
 *   - SearchResultTableComponent — the entry point
 *   - SearchResultRowComponent / TermListResultRowComponent — the two
 *     row variants the table picks between (standardRowType / termRowType)
 *   - InlineFavouriteComponent — heart icon column
 *   - InlinePlateRankComponent — admin-only rank badge column
 *   - InlinePlateViewerLinkComponent — plate-viewer eye icon column
 *   - SeeMoreButtonComponent — used inside TermListResultRowComponent
 *   - SignedUpContentComponent — gated-content wrapper, used inside the
 *     table footer / term-row preview
 *
 * Imports:
 *   - PlateComponentModule — every row renders `<pla-plate>` for the
 *     plate column; that lives in its own shared module already.
 *   - Material modules below cover every `mat-*` tag the templates use
 *     (table headers, paginator, slide toggles, icon buttons, spinner).
 *   - FormsModule — `[(ngModel)]` on the showFullSeries slide toggle.
 *
 * Exports only `SearchResultTableComponent`. The rows + inline
 * components are implementation detail of the table; consumers should
 * not place them directly in their templates.
 */
@NgModule({
  declarations: [
    SearchResultTableComponent,
    SearchResultRowComponent,
    TermListResultRowComponent,
    InlineFavouriteComponent,
    InlinePlateRankComponent,
    InlinePlateViewerLinkComponent,
    SeeMoreButtonComponent,
    SignedUpContentComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    PlateComponentModule,
  ],
  exports: [
    SearchResultTableComponent,
    // Other app.module-declared components still reference these inline
    // pieces directly in their templates (header, compare-results-table,
    // search-results-page, plate-detail-page, etc.), so we re-export them
    // here. Once those consumers migrate to <pla-search-result-table>,
    // these exports can drop.
    SearchResultRowComponent,
    TermListResultRowComponent,
    InlineFavouriteComponent,
    InlinePlateRankComponent,
    InlinePlateViewerLinkComponent,
    SeeMoreButtonComponent,
    SignedUpContentComponent,
  ],
})
export class SearchResultTableModule {}
